import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminSectionCard, {
  AdminButton,
  AdminEmptyState,
  AdminScrollTable,
  AdminStatCard,
  AdminStatusBadge,
} from "./AdminSectionCard";
import {
  useGetAdminContestQuery,
  useGetAdminContestRegistrationsQuery,
} from "../../features/api/appApi";
import {
  STATUS_OPTIONS,
  statusTone,
  sortedParticipants,
  errMsg,
  ManageRegistrationModal,
} from "./AdminContestRegistrationShared";

const roleLabel = (role) => (role === "leader" ? "Leader" : role === "individual" ? "Entrant" : "Member");

export default function AdminContestParticipantsPage() {
  const { contestId } = useParams();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [selectedReg, setSelectedReg] = useState(null);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const {
    data: contest,
    isLoading: contestLoading,
    error: contestError,
    refetch: refetchContest,
  } = useGetAdminContestQuery(contestId);

  const {
    data,
    isLoading,
    isFetching,
    error: listError,
    refetch: refetchRegistrations,
  } = useGetAdminContestRegistrationsQuery({
    contestId,
    search,
    status,
    sortBy,
    sortOrder,
    limit: 2000,
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    setError("");
    try {
      await Promise.all([refetchContest(), refetchRegistrations()]);
      setSuccess("Data table refreshed.");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setRefreshing(false);
    }
  };

  const stats = data?.stats || { total: 0, verified: 0, pending: 0, rejected: 0 };

  const participantRows = useMemo(
    () => {
      const registrations = data?.registrations || [];
      return registrations.flatMap((reg) =>
        sortedParticipants(reg).map((p) => ({ participant: p, registration: reg }))
      );
    },
    [data?.registrations]
  );

  useEffect(() => {
    if (listError) setError(errMsg(listError));
  }, [listError]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  if (contestLoading) {
    return <div className="py-12 text-center text-sm font-semibold text-slate-500">Loading contest...</div>;
  }

  if (contestError || !contest) {
    return (
      <div className="space-y-4">
        <Link to="/admin/contests" className="text-sm font-semibold text-blue-600 hover:text-blue-700">
          ← Back to Contests
        </Link>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">
          {errMsg(contestError) || "Contest not found."}
        </div>
      </div>
    );
  }

  const isTeam = contest.type === "team";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/admin/contests"
            className="mb-2 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            ← Back to Contests
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-black tracking-tight text-slate-900">{contest.title} — Participants</h1>
            <AdminStatusBadge tone="blue">
              {isTeam
                ? `Team (${contest.minTeamSize || 1}–${contest.maxTeamSize || 1} members)`
                : "Individual"}
            </AdminStatusBadge>
            <AdminStatusBadge tone={contest.editingEnabled ? "green" : "slate"}>
              Self-edit: {contest.editingEnabled ? "Unlocked" : "Locked"}
            </AdminStatusBadge>
          </div>
        </div>
        <Link to={`/admin/contests/${contestId}`}>
          <AdminButton variant="secondary">View Registrations</AdminButton>
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdminStatCard title="Total Registrations" value={stats.total} tone="blue" />
        <AdminStatCard title="Verified" value={stats.verified} tone="green" />
        <AdminStatCard title="Pending" value={stats.pending} tone="amber" />
        <AdminStatCard title="Total Participants" value={participantRows.length} tone="slate" />
      </div>

      {success ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
          {success}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">
          {error}
        </div>
      ) : null}

      <AdminSectionCard
        title="Participants"
        subtitle="Every individual participant across all registrations for this contest"
        rightAction={
          <AdminButton
            type="button"
            variant="secondary"
            onClick={handleRefresh}
            disabled={refreshing || isLoading}
            className="h-9 px-3 text-xs"
          >
            <span className={refreshing ? "inline-block animate-spin" : "inline-block"}>⟳</span>
            <span className="ml-1.5">{refreshing ? "Refreshing..." : "Refresh"}</span>
          </AdminButton>
        }
      >
        <form onSubmit={handleSearchSubmit} className="mb-5 flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            placeholder="Search by name, email, phone, ID, team name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          />
          <div className="flex flex-wrap gap-2">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="createdAt">Date Submitted</option>
              {isTeam ? <option value="teamName">Team Name</option> : null}
              <option value="status">Status</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="DESC">Descending</option>
              <option value="ASC">Ascending</option>
            </select>
            <AdminButton type="submit">Search</AdminButton>
          </div>
        </form>

        {isLoading || isFetching ? (
          <div className="py-12 text-center text-sm font-semibold text-slate-500">Loading participants...</div>
        ) : participantRows.length > 0 ? (
          <AdminScrollTable>
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Student ID</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Department</th>
                  <th className="px-4 py-3">T-shirt</th>
                  {isTeam ? <th className="px-4 py-3">Team</th> : null}
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {participantRows.map(({ participant: p, registration: reg }) => (
                  <tr
                    key={p.id}
                    className="cursor-pointer bg-white hover:bg-slate-50/70"
                    onClick={() => setSelectedReg(reg)}
                  >
                    <td className="px-4 py-4 font-semibold text-slate-800">{p.name}</td>
                    <td className="px-4 py-4 text-xs text-slate-600">{roleLabel(p.role)}</td>
                    <td className="px-4 py-4 font-mono text-xs text-slate-600">{p.studentId}</td>
                    <td className="px-4 py-4 text-xs text-slate-600">{p.email}</td>
                    <td className="px-4 py-4 text-xs text-slate-600">{p.phone}</td>
                    <td className="px-4 py-4 text-xs text-slate-600">{p.department}</td>
                    <td className="px-4 py-4 text-xs text-slate-600">
                      {p.tshirtSize || <span className="text-slate-300">—</span>}
                    </td>
                    {isTeam ? (
                      <td className="px-4 py-4 text-xs text-slate-600">
                        {reg.teamName}
                        <div className="font-mono text-[10px] text-slate-400">{reg.registrationCode}</div>
                      </td>
                    ) : null}
                    <td className="px-4 py-4">
                      <AdminStatusBadge tone={statusTone(reg.status)}>{reg.status}</AdminStatusBadge>
                    </td>
                    <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <AdminButton onClick={() => setSelectedReg(reg)} className="h-9 px-3 text-xs">
                        Manage
                      </AdminButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminScrollTable>
        ) : (
          <AdminEmptyState text="No participants match the query." />
        )}
      </AdminSectionCard>

      {selectedReg ? (
        <ManageRegistrationModal
          contest={contest}
          registration={selectedReg}
          onClose={() => setSelectedReg(null)}
          onUpdated={(msg) => {
            setSelectedReg(null);
            setSuccess(msg);
          }}
          onDeleted={(msg) => {
            setSelectedReg(null);
            setSuccess(msg);
          }}
        />
      ) : null}
    </div>
  );
}
